import { TrackEvent } from '../../enums/Content/TrackEvent';
import TrackInterface from '../../graphql/Rooms/TrackInterface';
import Poll from '../Poll';

type MediaTrackerTarget = HTMLAudioElement | HTMLVideoElement | null;

interface SubscribableMediaEvent {
  name: string;
  stopPolling: boolean;
}

const SUBSCRIBABLE_EVENTS: SubscribableMediaEvent[] = [
  /**
   * Tracking `playing` since it's fired by the media tag in more scenarios
   * than the `play` event is fired. For example:
   *  1. When the media first starts playing.
   *  2. When the media is paused and then resumed.
   *  3. When the media is playing and the user rewinds/forwards with the mouse.
   *  4. When the media is playing and the user rewinds/forwards it via
   *     keyboard arrows (in this scenario, the `play` event isn't fired).
   * In any case, this is the most reliable event to track the spent time, since
   * we can calculate the time the media started playing and when it stops.
   */
  { name: 'playing', stopPolling: false },
  /**
   * The pause event is fired when the user pauses the media:
   *  1. With the mouse, by clicking anywhere in the video (except control bar).
   *  2. With the mouse, by clicking on the pause button on control bar.
   *  3. With the keyboard's space bar.
   *  4. When the user rewinds/forwards the media with the control bar.
   *  5. When the media ends. However, this is not always the case, the media
   *     could end and the pause event could not be fired.
   * This event stops the polling since we need to know the elapsed time since
   * the latest polling tick (when the media was latest resumed) and the moment
   * it's been paused.
   */
  { name: 'pause', stopPolling: true },
  /**
   * The `ended` event is self explanatory, it's fired by the media tag when
   * the media has reached its reproduction length. It stops the polling just
   * in case the `pause` event isn't fired, to measure the elapsed time since
   * the latest polling tick and the end of the media. This is useful for
   * scenarios where the media length is not module (%) of the poll interval.
   * (e.g. if the media length is 21 and the interval is every 5 seconds)
   */
  { name: 'ended', stopPolling: true },
  /**
   * Tracking `seeking` instead of `seeked` since it's fired immediately when
   * the user rewinds/forwards the media with either the control bar or the
   * keyboard. The `seeked` event is not reliable because there are several
   * scenarios that are better handled with this event (e.g. if the media is
   * playing or not when seeking with keyboard).
   * If the user rewinds/forwards the media, the polling is stopped and it's
   * ticked immediately, since this action affects the media reproduction timeline.
   */
  { name: 'seeking', stopPolling: true },
];

/**
 * Starting tracking from 1 since we're not expecting analytics to be 0-indexed
 */
const INITIAL_SECOND = 1;

export default class MediaTracker {
  private active = false;

  private poll: Poll | null = null;

  private target: MediaTrackerTarget = null;

  private initialMediaTime = INITIAL_SECOND;

  private mediaTimeBeforeDelta = INITIAL_SECOND;

  private mediaTimeAfterDelta = INITIAL_SECOND;

  constructor(
    event: TrackEvent,
    contentId: string,
    contactId?: string,
    roomId?: string,
    version?: number
  ) {
    this.poll = new Poll((ellapsedTime: number) =>
      this.trackEvent(
        event,
        contentId,
        contactId,
        roomId,
        version,
        ellapsedTime
      )
    );
  }

  /**
   * Sets up the listeners for the media target (HTML video or audio tags),
   * adding the handlers for the most relevant events that have an effect
   * on the tracking.
   */
  public attach(target: MediaTrackerTarget) {
    this.target = target;
    this.setupListeners('addEventListener');
  }

  /**
   * Removes the listeners for the media target. This must be called when
   * the parent component is destroyed, in order to avoid duplicated
   * tracking.
   */
  public detach() {
    this.poll?.detach();
    this.setupListeners('removeEventListener');
  }

  /**
   * Controls if the tracking should continue. This is useful to determine
   * if the media is within the viewport and activating/deactivating the
   * tracking based on that.
   */
  public setActive(active: boolean) {
    this.active = active;

    if (!this.active) {
      this.poll?.pause();
      return;
    }

    /**
     * Only resume the tracking if the tracker has been set to active (e.g. if
     * the media is within the viewport) AND the media is still playing,
     * because if the player was paused, exited the viewport and then became
     * visible but it's still paused, the tracking must be resumed whenever
     * the user starts playing again.
     */
    if (!this.target?.paused) {
      this.resume();
    }
  }

  /**
   * Ensures the obtained target current time is at least the initial second,
   * since we don't want to have analytics being 0-indexed
   */
  private getCurrentTargetTime(): number {
    if (!this.target) {
      return INITIAL_SECOND;
    }

    return this.target.currentTime >= 1
      ? this.target.currentTime
      : INITIAL_SECOND;
  }

  /**
   * Attaches or detaches the media event listeners to the target. Additionally,
   * it sets up the document's vibilitychange listener, to determine if
   * the media must be paused when the browser's tab is changed.
   */
  private setupListeners(method: 'addEventListener' | 'removeEventListener') {
    if (!this.target) {
      return;
    }

    this.target![method]('timeupdate', this.timeUpdateHandler.bind(this));

    SUBSCRIBABLE_EVENTS.forEach((event) => {
      this.target![method](
        event.name,
        this.mediaEventHandler.bind(this, event)
      );
    });
  }

  /**
   * Generic event handler for the media tag (audio or video). It's just
   * in charge of pausing/resuming the poll depending on the event that
   * has been triggered. Check SUBSCRIBABLE_EVENTS for a detailed explanation
   * on the tracked events and whether they pause or resume the poll.
   */
  private mediaEventHandler(event: SubscribableMediaEvent) {
    if (event.stopPolling) {
      this.setActive(false);
      return;
    }

    this.setActive(true);
  }

  /**
   * Keeps a record of the media time changes, saving both the value before
   * the update and the value after the latest update. These updates happen
   * for two reasons:
   *  1. Around every 0.25s automatically while the media is playing
   *  2. If the user forwards or rewinds the media
   * Saving the time before and after the delta allow us to keep the current time
   * BEFORE the user seeks to another position in the media, which is useful
   * to determine the amount and which seconds were played.
   */
  private timeUpdateHandler() {
    const current = this.getCurrentTargetTime();
    this.mediaTimeBeforeDelta = this.mediaTimeAfterDelta;
    this.mediaTimeAfterDelta = current;
  }

  private resume() {
    this.initialMediaTime = this.getCurrentTargetTime();
    this.poll?.resume();
  }

  private trackEvent(
    event: TrackEvent,
    contentId: string,
    contactId: string | undefined,
    roomId: string | undefined,
    version: number | undefined,
    elapsedTime: number
  ) {
    const delta = this.mediaTimeAfterDelta - this.mediaTimeBeforeDelta;
    const start = this.initialMediaTime;

    /**
     * The normal delta is around 0.25s, so we'll consider any change beyond that
     * to be a skip forwards or backwards on the media. Since it's a forced
     * change, the second to last update (the media time before delta) contains
     * the current time that was played BEFORE that forced update, therefore, we
     * use that value instead of the media current time, which will pointing
     * to the time the user skipped to.
     */
    const skipping = Math.abs(delta) > 1;

    let end = this.mediaTimeAfterDelta;
    if (skipping) {
      end = this.mediaTimeBeforeDelta;
    }

    /**
     * If the tracking comes from a normal reproduction time and a poll tick,
     * we increase by 1 the end time to create inclusive intervals, such as
     * [1, 5], [6, 10]. But if it comes from a rewind/forward action, we
     * use the value as it is since that action itself creates inclusive
     * independent intervals, for example: [21, 25] (rewind) [3, 8].
     */
    this.initialMediaTime = skipping ? end : Math.round(end + 1);

    if (!(roomId && version)) {
      return;
    }

    try {
      new TrackInterface(contactId).track({
        contentId,
        event,
        measure: elapsedTime,
        roomId,
        version,
        metadata: {
          start: Math.floor(start),
          end: Math.round(end),
        },
      });
    } catch (e) {
      // user shouldn't be alerted about this fail, but we're catching
      // this exception to avoid "Uncaught promise reject" warnings
    }
  }
}
